import moment from 'moment'
import { formatDate } from '@/configs/dates'
import statuses, { list as statusesList } from '@/configs/statuses'
import {
  gasCylinderEquipmentCertificationTypes,
  gceSpecialistCAUserType,
  isGasCylinderEquipmentCertificationType,
  isGceSpecialistUserType,
  vehicleConditionTypes
} from '@/configs/constants'
import {
  certificationAgencyRoles,
  laboratoryRoles,
  statementFilterFields,
  statementSortFields,
  statementStatusIds
} from '@/configs/statementList'

export function isZeroHeadlightCount (count) {
  return count !== null && count !== '' && Number(count) === 0
}

export function getFileType (link) {
  let type = link.match(/([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gmi)[0]
  let possibleTypes = {
    video: ['mp4', 'webm'],
    image: ['png', 'jpeg', 'jpg'],
    pdf: ['pdf'],
    document: ['doc', 'docx']
  }
  for (let fileType in possibleTypes) {
    if (possibleTypes[fileType].includes(type)) return fileType
  }
}
/*
 
  Ця функція призначена для отримання правильної форми іменника в залежності від числа.

  number: Це число, для якого потрібно отримати форму іменника.
          Функція визначає, яку форму використовувати на основі цього числа.

  singular: Це форма іменника для випадку, коли число дорівнює 1.
            Наприклад, "сторінка" або "елемент".

  plural1: Це форма іменника для випадку, коли число дорівнює 2, 3 або 4.
            Наприклад, "сторінки" або "елементи".

  plural2: Це форма іменника для випадку, коли число не підпадає під жодну з попередніх категорій.
            Наприклад, "сторінок" або "елементів".
  
  Функція використовує останню цифру числа, а також останні дві цифри для визначення правильної форми іменника
  з урахуванням англійського винятку для чисел від 11 до 19.
*/
export function getNounForm(number, singular, plural1, plural2) {
    // Визначення останньої цифри числа (для англійської - також перевірка на 11-19)
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return plural2;
    }

    if (lastDigit === 1) {
        return singular;
    } else if (lastDigit >= 2 && lastDigit <= 4) {
        return plural1;
    } else {
        return plural2;
    }
}

// Ця функція призначена для капіталізації першої літери слова.
export function capitalizeFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function generateFilterParams(filter) {
  if (filter && Object.keys(filter).length) {
    const params = new URLSearchParams()
    for (let field in filter) {
      if (filter[field] || filter[field] === false || filter[field] === 0) params.append(field, filter[field])
    }
    return '?' + params
  }
  return ''
}

export function scrollToError (gap = 30) {
  const errorField = document.getElementsByClassName('error--text')[0]
  if (errorField) {
    const headerHeight = 48 
    const elementPosition = errorField.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerHeight - gap 
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
}

// Визначає групу ОС або ВЛ за роллю профілю.
export const statementListRole = type => certificationAgencyRoles.includes(type)
  ? 'OC'
  : laboratoryRoles.includes(type) ? 'VL' : null

// Для спеціалістів ГБО ОС та ВЛ залишає лише типи сертифікації ГБО.
export function getStatementCertificationTypes (typeUser) {
  let types = vehicleConditionTypes
  if (isGceSpecialistUserType(typeUser)) {
    types = types.filter(type => gasCylinderEquipmentCertificationTypes.includes(type.id))
  }
  return types.map(type => ({ value: type.id, text: type.shortName }))
}

export function canViewStatement (typeUser, statement) {
  return typeUser !== gceSpecialistCAUserType ||
    isGasCylinderEquipmentCertificationType(statement?.vehicle_condition)
}

export function canViewIdentificationAct (statement) {
  return ![
    statusesList.statementCreateByLaboratory,
    statusesList.statementWaitForClientSignature,
    statusesList.statementWaitSignatureAuditor,
    statusesList.statementWaitSignatureManager,
    statusesList.statementWaitForCertificationAgency
  ].includes(statement.status_document)
}

// Спеціаліст ОС обробляє заявку ГБО до завершення підписання рішення та появи огляду.
export function canProcessGceStatement (statement) {
  return isGasCylinderEquipmentCertificationType(statement?.vehicle_condition) &&
    !statement.inspection && [
      // Попередні етапи залишено для можливого повернення доступу:
      // statusesList.statementCreatedByLaboratory,
      // statusesList.statementWaitForClientSignature,
      // statusesList.statementWaitSignatureAuditor,
      // statusesList.identificationActWaitDecisionAuditor,
      statusesList.identificationActWaitSignatureForAuditor,
      statusesList.statementWaitForCategoryChoices,
      statusesList.statementWaitForLaboratory,
      statusesList.statementWaitForDecision,
      statusesList.statementDecisionWaitSignature,
      statusesList.statementDecisionWaitForDirectorSignature
    ].includes(statement.status_document)
}

// Повертає спільні та доступні групі поля фільтрів.
export function getStatementFilterFields (role) {
  return statementFilterFields.filter(field => !field.role || field.role === role)
}

// Формує колонки таблиці відповідно до групи ОС або ВЛ.
export function getStatementHeaders (translate, role, showCertificateDsbtTransfer = false) {
  const headers = [
    { text: translate('number'), value: 'number', nowrap: true, filterKeys: ['number'] },
    { text: translate('mark'), value: 'markWithModel', nowrap: true, filterKeys: ['vehicle_mark_name'] },
    { text: translate('vinCode'), value: 'vin_code', nowrap: true, filterKeys: ['vin_code'] },
    { text: translate('client'), value: 'clientAbbr', filterKeys: ['client_full_name'] }
  ]
  if (role === 'OC' || role === 'VL') {
    headers.push(
      { text: 'Тип сертифікації', value: 'vehicle_condition', nowrap: true, filterKeys: ['vehicle_condition'] },
      { text: 'Дата заявки', value: 'created_at', nowrap: true, filterKeys: ['created_at_range'] }
    )
    if (role === 'OC') headers.push({ text: 'Назва ВЛ', value: 'laboratory_name', filterKeys: ['laboratory'] })
    headers.push({
      text: '№ дільниці', value: 'laboratory_branch_office_number', nowrap: true, filterKeys: ['laboratory_branch_office']
    })
  }
  if (showCertificateDsbtTransfer) {
    headers.push({
      text: translate('certificateDsbtTransfer.label'),
      value: 'transferred_to_transport_service',
      filterKeys: ['transferred_to_transport_service'],
      nowrap: true
    })
  }
  headers.push({
    text: translate('status'),
    value: 'status_statement',
    constructorLocked: true,
    nowrap: true,
    copyable: false,
    filterKeys: ['status_document']
  })
  return headers
}

// Формує варіанти статусів із перекладеними назвами та, за потреби, обмежує їх доступними ID.
export function getStatementStatusOptions (translate, availableStatusIds = statementStatusIds) {
  const allowedStatusIds = new Set(availableStatusIds
    .map(value => parseStatementInteger(String(value)))
    .filter(value => statementStatusIds.includes(value)))

  return statementStatusIds.filter(value => allowedStatusIds.has(value)).map(value => {
    const status = statuses.statement(value)

    return {
      value,
      text: translate(status.text),
      hint: status.hint ? translate(status.hint) : ''
    }
  })
}

// Повертає назви ВЛ або номери дільниць із довідника зі значеннями ID.
export function getStatementDirectoryOptions (items = []) {
  const options = new Map()
  for (const item of items) {
    const name = String(item.name ?? '').trim()
    const id = parseStatementInteger(String(item.id ?? ''))
    if (name && id !== null) options.set(id, { value: id, text: name })
  }
  return [...options.values()]
}

// Для ОС доповнює номер дільниці короткою назвою ВЛ; значенням лишається ID дільниці.
export function getStatementBranchOptions (branches = [], includeLaboratoryName = false) {
  return getStatementDirectoryOptions(branches.map(branch => {
    const shortName = includeLaboratoryName ? String(branch.laboratory_short_name ?? '').trim() : ''
    const number = String(branch.name ?? '').trim()
    return { ...branch, name: number && shortName ? `${number} (${shortName})` : number }
  }))
}

// Формує баджі застосованих значень; період дат об'єднує в один бадж.
export function getStatementFilterChips (filters, fields, options = {}) {
  const chips = []
  for (const field of fields) {
    const value = filters[field.key]
    if (field.type === 'date') {
      const [from, to] = value || []
      let text = ''
      if (from && from === to) text = formatDate(from)
      else if (from && to) text = `${formatDate(from)} — ${formatDate(to)}`
      else if (from) text = `від ${formatDate(from)}`
      else if (to) text = `до ${formatDate(to)}`
      if (text) chips.push({ key: field.key, label: field.label, value: null, text })
      continue
    }

    if (field.type === 'boolean') {
      if (value === true || value === false) {
        const text = field.options.find(option => option.value === value)?.text ?? String(value)
        chips.push({ key: field.key, label: field.label, value, text })
      }
      continue
    }

    if (value === undefined || value === null || value === '') continue
    const values = Array.isArray(value) ? value : [value]
    const directory = options[field.directory] || []
    for (const selected of values) {
      const option = directory.find(option => option.value === selected)
      const text = option?.text ?? String(selected)
      chips.push({ key: field.key, label: field.label, value: selected, text, hint: option?.hint || '' })
    }
  }
  return chips
}

// Перетворює коректну дату на YYYY-MM-DD; для некоректної повертає null.
export function normalizeStatementDate (value) {
  if (!value || typeof value !== 'string') return null
  const date = moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY'], true)
  return date.isValid() ? date.format('YYYY-MM-DD') : null
}

// Нормалізує діапазон у дві межі; одна дата задає обидві, порожня межа лишається відкритою.
export function normalizeStatementDateRange (value) {
  const parts = (Array.isArray(value) ? value : String(value ?? '').split(',')).map(value => String(value ?? '').trim())
  if (parts.length < 1 || parts.length > 2) return null
  const from = normalizeStatementDate(parts[0])
  const to = parts.length === 1 ? from : normalizeStatementDate(parts[1])
  if ((parts[0] && !from) || (parts[1] && !to) || (!from && !to)) return null
  return from && to && from > to ? [to, from] : [from || '', to || '']
}

// Відновлює фільтри з query, нормалізуючи типи та відкидаючи недоступні значення.
export function readStatementFilters (query = {}, role, certificationTypes = getStatementCertificationTypes()) {
  const filters = {}
  for (const field of getStatementFilterFields(role)) {
    const raw = query[field.key]
    if (raw === undefined || raw === null) continue
    if (field.type === 'date') {
      const value = normalizeStatementDateRange(raw)
      if (value) filters[field.key] = value
      continue
    }
    if (field.type === 'boolean') {
      const value = readBooleanQuery(raw)
      if (value !== null) filters[field.key] = value
      continue
    }
    const parts = Array.isArray(raw) ? raw : [raw]

    if (field.type !== 'multi') {
      const firstValue = parts.find(value => value !== null && value !== undefined)
      const value = normalizeStatementFilterValue(firstValue, field, certificationTypes)
      if (value !== null) filters[field.key] = value
      continue
    }

    const selectedValues = new Set()
    for (const part of parts) {
      if (part === null || part === undefined) continue
      const values = String(part).split(',')
      for (const rawValue of values) {
        const value = normalizeStatementFilterValue(rawValue, field, certificationTypes)
        if (value !== null) selectedValues.add(value)
      }
    }
    if (selectedValues.size > 0) filters[field.key] = [...selectedValues]
  }

  return filters
}

// Оновлює задані поля фільтрів, зберігаючи інші query-параметри.
export function mergeStatementFilterQuery (query = {}, filters = {}, fields = statementFilterFields) {
  const next = { ...query }
  fields.forEach(({ key }) => {
    delete next[key]
    const value = filters[key]
    if (value === undefined || value === null || value === '') return

    if (Array.isArray(value)) {
      if (value.length === 0) return
      next[key] = value.join(',')
      return
    }
    next[key] = String(value)
  })
  return next
}

// Читає boolean із query; відсутнє або некоректне значення повертає як null.
export function readBooleanQuery (value) {
  const rawValue = firstQueryValue(value)
  if (rawValue === true || rawValue === 'true') return true
  if (rawValue === false || rawValue === 'false') return false
  return null
}

// Відновлює пагінацію та явно задане сортування з query.
export function readStatementOptions (query = {}, headers = []) {
  const ordering = String(firstQueryValue(query.ordering) || '')
  const requestedField = ordering.replace(/^-/, '')
  const sortKey = Object.keys(statementSortFields).find(key =>
    statementSortFields[key] === requestedField && headers.some(header => header.value === key)
  )
  const pageSize = positiveQueryInteger(query.page_size, 10)
  return {
    page: positiveQueryInteger(query.page, 1),
    itemsPerPage: [5, 10, 20, 50].includes(pageSize) ? pageSize : 10,
    sortBy: sortKey ? [sortKey] : [],
    sortDesc: sortKey ? [ordering.startsWith('-')] : []
  }
}

// Формує ordering для API; без вибраного сортування повертає null.
export function statementOrdering (options) {
  const field = statementSortFields[options.sortBy?.[0]]
  return field ? `${options.sortDesc?.[0] ? '-' : ''}${field}` : null
}

// Формує query списку заяв із множинними значеннями через кому.
export function statementFilterParams (filters = {}) {
  const params = new URLSearchParams()
  const queryFilters = mergeStatementFilterQuery({}, filters)
  for (const key of ['page', 'page_size', 'ordering', 'can_sign']) {
    const value = filters[key]
    if (value !== undefined && value !== null && value !== '') queryFilters[key] = value
  }
  for (const [key, value] of Object.entries(queryFilters)) params.append(key, value)
  const query = params.toString()
  return query ? `?${query}` : ''
}

// Очищає одне значення та перевіряє його тип і доступність у довіднику.
function normalizeStatementFilterValue (rawValue, field, certificationTypes) {
  if (rawValue === null || rawValue === undefined) return null
  const text = String(rawValue).trim()
  if (text === '') return null

  let value = text
  if (field.type === 'number' || field.numeric) {
    value = parseStatementInteger(text)
    if (value === null) return null
  }

  if (field.key === 'status_document' && !statementStatusIds.includes(value)) return null
  if (field.key === 'vehicle_condition') {
    const isAllowedType = certificationTypes.some(type => type.value === value)
    if (!isAllowedType) return null
  }
  return value
}

const firstQueryValue = value => Array.isArray(value) ? value[0] : value

function parseStatementInteger (value) {
  if (!/^\d+$/.test(value)) return null
  const number = Number(value)
  return Number.isSafeInteger(number) ? number : null
}

function positiveQueryInteger (value, fallback) {
  const text = String(firstQueryValue(value) ?? '')
  const number = parseStatementInteger(text)
  if (number === null || number <= 0) return fallback
  return number
}
